import React, { useState } from 'react';
import { cn } from '@/lib/utils';

interface OptimizedImageProps {
  src: string;
  alt: string;
  className?: string;
  width?: number;
  height?: number;
  loading?: 'eager' | 'lazy';
  onError?: (e: React.SyntheticEvent<HTMLImageElement>) => void;
}

export const OptimizedImage = ({ 
  src, 
  alt, 
  className, 
  width, 
  height, 
  loading = 'lazy',
  onError 
}: OptimizedImageProps) => {
  const [imageError, setImageError] = useState(false);
  const [webpSupported, setWebpSupported] = useState<boolean | null>(null);

  // Check WebP support
  React.useEffect(() => {
    const checkWebPSupport = () => {
      const canvas = document.createElement('canvas');
      canvas.width = 1;
      canvas.height = 1;
      const dataURL = canvas.toDataURL('image/webp');
      setWebpSupported(dataURL.indexOf('data:image/webp') === 0);
    };
    checkWebPSupport();
  }, []);

  const handleError = (e: React.SyntheticEvent<HTMLImageElement>) => {
    setImageError(true);
    onError?.(e);
  };

  // Generate responsive image props
  const getOptimizedSrc = (originalSrc: string) => {
    // For external images, return as-is since we can't control them
    if (originalSrc.startsWith('http') && !originalSrc.includes('lovable-uploads')) {
      return originalSrc;
    }

    // For local uploads, we can add query parameters for optimization if supported
    if (originalSrc.includes('lovable-uploads')) {
      const url = new URL(originalSrc, window.location.origin);
      if (width) url.searchParams.set('w', width.toString());
      if (height) url.searchParams.set('h', height.toString());
      url.searchParams.set('q', '85'); // Quality 85%
      return url.toString();
    }

    return originalSrc;
  };

  const optimizedSrc = imageError ? '/placeholder.svg' : getOptimizedSrc(src);

  return (
    <img
      src={optimizedSrc}
      alt={alt}
      className={cn(className)}
      width={width}
      height={height}
      loading={loading}
      onError={handleError}
      style={{
        ...(width && height && { aspectRatio: `${width}/${height}` })
      }}
    />
  );
};